import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { GOOGLE_AUTH_ADAPTER } from '../tokens/infrastructure.tokens';
import { NotificationService } from './notification.service';
import { TranslationService } from './translation.service';

/**
 * Handles the OAuth deep-link callback on Capacitor (Android / iOS).
 *
 * Flow:
 *  1. `authorizeWithCodeFlow` opens a Chrome Custom Tab via `Browser.open()`.
 *  2. Google redirects to the HTTPS redirect URI
 *     `https://personal-finance.devenvhub.com/oauth-callback?code=…&state=…`.
 *  3. Firebase Hosting serves `oauth-callback.html` at that path.
 *  4. `oauth-callback.html` redirects to the Android intent URL:
 *     `intent://oauth-callback?code=…&state=…#Intent;scheme=<runtime-app-id>;package=<runtime-app-id>;S.browser_fallback_url=…;end`
 *  5. Chrome Custom Tabs fires the intent, opens the app and **closes the Custom Tab**
 *     automatically. Capacitor's `appUrlOpen` event fires with the URL
 *     `<runtime-app-id>://oauth-callback?code=…&state=…`.
 *  6. This service parses the URL, calls `handleOAuthCallback`, and navigates
 *     to the result screen — mirroring the web `oauthCallbackGuard` behaviour.
 *  7. Non-Android browsers follow `S.browser_fallback_url` to `/oauth-callback-web`
 *     where the Angular SPA `oauthCallbackGuard` handles the code exchange.
 *
 * Only registered when `runtimeKind === 'capacitor'`.
 */
@Injectable({ providedIn: 'root' })
export class CapacitorOAuthHandlerService {
  private readonly googleAuth = inject(GOOGLE_AUTH_ADAPTER);
  private readonly router = inject(Router);
  private readonly notification = inject(NotificationService);
  private readonly translation = inject(TranslationService);

  initialize(): void {
    App.addListener('appUrlOpen', (event) => this.handleAppUrlOpen(event.url));
  }

  private async handleAppUrlOpen(urlString: string): Promise<void> {
    console.log('[CapacitorOAuth] appUrlOpen fired, raw URL:', urlString);
    let url: URL;
    try {
      url = new URL(urlString);
    } catch (error) {
      console.warn('[CapacitorOAuth] unparseable URL:', urlString, error);
      return;
    }

    console.log('[CapacitorOAuth] parsed — scheme:', url.protocol, '| host:', url.host, '| pathname:', url.pathname);

    // Only handle OAuth callback URLs.
    // intent:// delivery (double-slash): com.devenvhub.personalfinance://oauth-callback?…
    //   → url.host === 'oauth-callback'
    // Direct custom-scheme (single-slash, legacy): com.devenvhub.personalfinance:/oauth-callback?…
    //   → url.pathname.startsWith('/oauth-callback')
    if (url.host !== 'oauth-callback' && !url.pathname.startsWith('/oauth-callback')) {
      console.log('[CapacitorOAuth] URL does not match OAuth callback pattern — ignoring');
      return;
    }

    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    const error = url.searchParams.get('error');

    console.log(
      '[CapacitorOAuth] callback params — code present:',
      !!code,
      '| state present:',
      !!state,
      '| error:',
      error ?? 'none',
    );

    // Close the Custom Tab before navigating
    await Browser.close();
    console.log('[CapacitorOAuth] Custom Tab closed');

    if (error) {
      console.warn('[CapacitorOAuth] OAuth cancelled by user:', error);
      this.notification.error(this.translation.instant('common.notifications.oauth_cancelled'));
      await this.router.navigate(['/']);
      return;
    }

    if (!code || !state) {
      console.warn('[CapacitorOAuth] OAuth callback missing code or state parameter');
      this.notification.error(this.translation.instant('common.notifications.oauth_invalid_params'));
      await this.router.navigate(['/']);
      return;
    }

    console.log('[CapacitorOAuth] Calling handleOAuthCallback...');
    try {
      const result = await this.googleAuth.handleOAuthCallback(code, state);
      console.log(
        '[CapacitorOAuth] handleOAuthCallback result — accessToken present:',
        !!result?.accessToken,
        '| customState:',
        JSON.stringify(result?.customState),
      );
      const customState = result?.customState ?? {};
      const target = (customState['redirect'] as string) ?? '/';

      if (result?.accessToken) {
        console.log('[CapacitorOAuth] ✓ OAuth authorization successful (Capacitor), navigating to:', target);
        await this.router.navigateByUrl(target, { state: { oauthSuccess: true, ...customState } });
        return;
      }

      console.warn('[CapacitorOAuth] handleOAuthCallback returned no accessToken');
      this.notification.error(this.translation.instant('common.notifications.oauth_failed'));
      await this.router.navigateByUrl(target, { state: { oauthSuccess: false, ...customState } });
    } catch (err) {
      console.error('[CapacitorOAuth] OAuth callback error (Capacitor):', err);
      this.notification.error(this.translation.instant('common.notifications.oauth_error'));
      await this.router.navigate(['/']);
    }
  }
}
